import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Controller, useFormContext } from 'react-hook-form';

export type MuiFormAutocompleteOptionT = {
  value: string | number;
  label: string;
};

type MuiFormAutocompletePropsT = {
  name: string;
  label: string;
  options: MuiFormAutocompleteOptionT[];
  placeholder?: string;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  allowCustomInput?: boolean;
};

const MuiFormAutocomplete = ({
  name,
  label,
  options,
  placeholder = '',
  isLoading = false,
  required = false,
  disabled = false,
  allowCustomInput = true,
}: MuiFormAutocompletePropsT): JSX.Element => {
  const { control } = useFormContext();

  const optionLabels = options.map(o => (typeof o.label === 'string' ? o.label : String(o.value)));

  const getDisplayValue = (v: unknown): string => {
    if (v == null || v === '') return '';
    const opt = options.find(o => o.value === v || o.label === v);
    if (opt == null) return String(v);
    return typeof opt.label === 'string' ? opt.label : String(opt.value);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field, fieldState }): React.ReactElement => (
        <Box>
          <Autocomplete
            freeSolo={allowCustomInput}
            options={optionLabels}
            slotProps={{
              paper: {
                sx: { width: 'max-content', minWidth: '100%' },
              },
            }}
            value={field.value != null && field.value !== '' ? getDisplayValue(field.value) : null}
            inputValue={
              field.value != null && field.value !== '' ? getDisplayValue(field.value) : ''
            }
            onInputChange={(_e, value): void => {
              field.onChange(value ?? '');
            }}
            onChange={(_e, value): void => {
              const next =
                typeof value === 'string'
                  ? value
                  : (value != null && typeof value === 'object' && 'label' in value
                      ? (value as { label?: string }).label
                      : '') ?? '';
              const option = options.find(o => o.label === next);
              const nextValue = option != null ? option.value : next;
              field.onChange(typeof nextValue === 'number' ? String(nextValue) : nextValue);
            }}
            onBlur={field.onBlur}
            disabled={disabled || isLoading}
            size="small"
            renderInput={(params): React.ReactElement => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? null}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '13px' },
                  '& .MuiInputLabel-root': { fontSize: '13px' },
                  '& .MuiFormHelperText-root': { fontSize: '11px' },
                }}
              />
            )}
          />
        </Box>
      )}
    />
  );
};

export default MuiFormAutocomplete;
